// import React from 'react';
import { getFlatExcelWorkbook } from '../util/excel';
import { saveAs } from 'file-saver';
import { getPincode, filterUnavailableReleases, filterPcsReleases, filterPinAllowed, filterAssignLocationsReleases } from '../util/pincode';
import useAuth from './useAuth';
import useIntl from './useIntl';
import useWalletError from './useWalletError';
import { checkAssignLocations } from '../services/releases';
// import useSentry from '../hooks/useSentry';

const useFlatExcel = (anonAddresses) => {
  const { user } = useAuth();
  const { translate } = useIntl();
  const onWalletError = useWalletError();
  // const logSentry = useSentry();

  /**
   * Download given excel workbook
   * @param {excel} workbook
   */
  const download = async (workbook) => {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'Releases.xlsx');
  };

  /**
   * flatten bill numbers
   * @param {Array[objects]} bill
   */
  const formatReleases = (bill) => {
    return bill.reduce(
      (result, { releases, ...bill }) =>
        result.concat(
          releases.map((release) => ({
            ...bill,
            ...release,
            releaseStatus: translate(`releaseStatus.${release.releaseStatus}`),
          }))
        ),
      []
    );
  };

  /**
   * Fetch pincodes and add pincode to each release
   * @param {Array of releases} releases
   */
  const addPincodeToReleases = async (releases, userCanFetchPincode) => {    
    const assignLocationsCheckResult = await checkAssignLocations({ releaseAddresses: releases.map(r => r.address), anonAddresses });
    
    let getPincodesForReleases = releases
      .filter(r => filterUnavailableReleases(r))
      .filter(r => filterPcsReleases(r))
      .filter(r => filterPinAllowed(assignLocationsCheckResult, user, r))
      .filter(r => filterAssignLocationsReleases(assignLocationsCheckResult, r, userCanFetchPincode))
      .map(({address, version}) => ({address, version}));

    let pincodeResults = await getPincode(getPincodesForReleases, user.organization);
    
    pincodeResults = pincodeResults
      // SCRDEV-1973: no pins for blocked containers - add replacement string for blocked containers
      .concat(
        releases
          .filter(r => !filterUnavailableReleases(r))
          .map(r => ({ address: r.address, error: 'Not allowed to fetch pincode'}))
      )
      // SCRDEV-3514: no pins for PCS releases
      .concat(
        releases
          .filter(r => filterUnavailableReleases(r))
          .filter(r => !filterPcsReleases(r))
          .map(r => ({ address: r.address, error: 'Release sent to PCS'}))
      )
      // Check pin allowed through assign locations
      .concat(
        releases
          .filter(r => filterUnavailableReleases(r))
          .filter(r => filterPcsReleases(r))
          .filter(r => !filterPinAllowed(assignLocationsCheckResult, user, r))
          .map(r => ({ address: r.address, error: 'Pins disabled'}))
      )
      // SCRDEV-2200: no pins for unassigned containers - add replacement string for these containers
      .concat(
        releases
          .filter(r => filterUnavailableReleases(r))
          .filter(r => filterPcsReleases(r))
          .filter(r => filterPinAllowed(assignLocationsCheckResult, user, r))
          .filter(r => !filterAssignLocationsReleases(assignLocationsCheckResult, r, userCanFetchPincode))
          .map(r => ({ address: r.address, error: (r.isSecurePickup && !userCanFetchPincode) ? 'Not allowed to fetch pincode' : 'Must assign to fetch pin'}))
      )

    return releases.map(release => { 
      let result = pincodeResults.find(res => res.address === release.address)
      release.pincode = result ? (result.pincode ? result.pincode : result.error) : undefined;
      return release;
    })
  };

  /**
   * It downloads releases in flat table format
   * @param {Array} data - data to be downloaded
   * @param {Boolean} fetchPincode
   */
  const flatExcel = async (data, fetchPincode, userCanFetchPincode) => {
    let releases = formatReleases(data);
    if (fetchPincode) {
      try {
        releases = await addPincodeToReleases(releases, userCanFetchPincode);
      } catch (e) {
        onWalletError(translate(e.message));
      }
    }

    await download(getFlatExcelWorkbook(releases, fetchPincode));
  };

  return flatExcel;
};

export default useFlatExcel;
